import React, { useState } from "react";

const stages = [
  { id: "young-stage-1", number: 1, title: "Retirement Planning with Constant Income" },
  { id: "young-stage-2", number: 2, title: "Incorporating Income Growth" },
  { id: "young-stage-3", number: 3, title: "Capital Preservation and Perpetual Income" },
  { id: "young-stage-4", number: 4, title: "Incorporating Existing Savings" },
  { id: "young-stage-5", number: 5, title: "Incorporating Inflation" },
];

function StageHeader({ number, title }) {
  return (
    <header className="stage-header">
      <span>Stage {number}</span>
      <h2>{title}</h2>
    </header>
  );
}

function BottomLine({ children }) {
  return (
    <div className="bottom-line">
      <span>Bottom line</span>
      {children}
    </div>
  );
}

function ChangesTable({ rows }) {
  return (
    <div className="example-table-wrap">
      <table className="example-table">
        <caption>Changes from the previous scenario</caption>
        <thead>
          <tr><th>Parameter</th><th>Previous value</th><th>New value</th></tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]}><th>{row[0]}</th><td>{row[1]}</td><td>{row[2]}</td></tr>
          ))}
        </tbody>
      </table>
      <p className="table-note">All other assumptions remain unchanged.</p>
    </div>
  );
}

function YoungProfessionalExample({ expanded, onToggle }) {
  const [activeStage, setActiveStage] = useState(1);

  const selectStage = (stageNumber) => {
    setActiveStage(stageNumber);
    const card = document.querySelector(".career-example");
    if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <article
      className={`career-example ${expanded ? "expanded" : "collapsed"}`}
      data-active-stage={activeStage}
    >
      <button
        type="button"
        className="career-example-toggle"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span><small>Career Stage</small><strong>Young Professional</strong></span>
        <span className="collapse-action">
          {expanded ? "Hide example" : "Show example"}
          <i aria-hidden="true">{expanded ? "−" : "+"}</i>
        </span>
      </button>

      {expanded && <div className="career-example-body">
        <div className="career-example-intro">
          <span className="career-badge">Building momentum</span>
          <h1>Young Professional</h1>
          <p>
            See how a shorter savings horizon, moderate income growth, existing
            assets, and inflation shape a developing retirement plan.
          </p>
        </div>

        <nav className="stage-index" aria-label="Young Professional stages" role="tablist">
          {stages.map((stage) => (
            <button
              key={stage.id}
              type="button"
              className={`stage-tab stage-tab-${stage.number} ${activeStage === stage.number ? "active" : ""}`}
              onClick={() => setActiveStage(stage.number)}
              aria-selected={activeStage === stage.number}
              role="tab"
            >
              <span>{stage.number}</span>{stage.title}
            </button>
          ))}
        </nav>

        <section id="young-stage-1" className={`example-stage ${activeStage === 1 ? "" : "stage-hidden"}`} role="tabpanel">
          <StageHeader number="1" title="Retirement Planning with Constant Income" />
          <p>
            You have been working for several years and are making steady progress
            in your career. You are now taking a more active role in planning for
            retirement and want to determine how much you should save each year.
          </p>
          <p>
            You currently earn <strong>$100,000 per year</strong>. Rather than
            replacing your entire income during retirement, you assume that 80%
            will maintain your desired lifestyle. This corresponds to a desired
            retirement income of <strong>$80,000 per year</strong>.
          </p>

          <div className="example-table-wrap">
            <table className="example-table assumptions-table">
              <caption>Scenario</caption>
              <tbody>
                <tr><th>Current Retirement Savings</th><td>$0</td></tr>
                <tr><th>Current Annual Income</th><td>$100,000</td></tr>
                <tr><th>Replacement Rate</th><td>80%</td></tr>
                <tr><th>Desired Retirement Income</th><td>$80,000</td></tr>
                <tr><th>Years Until Retirement</th><td>30</td></tr>
                <tr><th>Years in Retirement</th><td>25</td></tr>
                <tr><th>Real Investment Return</th><td>4.0%</td></tr>
                <tr><th>Real Income Growth</th><td>0.0%</td></tr>
                <tr><th>Inflation Rate</th><td>0.0%</td></tr>
              </tbody>
            </table>
          </div>

          <div className="example-question">
            <span>Question</span>
            <p>How much should you save each year to achieve this goal?</p>
          </div>
          <BottomLine>
            <p>
              Under these assumptions, you would need to save approximately
              <strong> $22,283 per year</strong>, or about <strong>22.28% of your
              income</strong>, to accumulate approximately <strong>$1,249,766</strong>
              by retirement.
            </p>
          </BottomLine>

          <h3>Understanding the results</h3>
          <p>
            This assumes that income remains constant in real terms and that you
            receive no Social Security, pension, or other retirement-plan income.
            With 30 years to save rather than 40, contributions have less time to
            earn returns, making the required rate relatively high. Inflation is
            zero, so all amounts are in today's dollars.
          </p>
          <div className="looking-ahead">
            <h3>Looking ahead</h3>
            <p>Next, we incorporate moderate real income growth and examine how it changes the annual savings requirement.</p>
          </div>
        </section>

        <section id="young-stage-2" className={`example-stage ${activeStage === 2 ? "" : "stage-hidden"}`} role="tabpanel">
          <StageHeader number="2" title="Incorporating Income Growth" />
          <p>
            Promotions, job changes, added responsibility, and professional
            development may continue to increase your income. A real income growth
            rate of approximately <strong>1% per year</strong> may be appropriate
            for a young professional who expects moderate advancement.
          </p>
          <ChangesTable rows={[["Real Income Growth", "0.0%", "1.0%"]]} />
          <BottomLine>
            <p>
              You would need to save approximately <strong>$19,779 during the
              first year</strong>, or about <strong>19.78% of current income</strong>,
              to accumulate the same fund of approximately <strong>$1,249,766</strong>.
              Annual contributions then increase at the same 1% real rate as income.
            </p>
          </BottomLine>
          <h3>Understanding the results</h3>
          <p>
            The retirement fund remains unchanged because the goal has not changed.
            Income growth instead changes the path: more lifetime savings can occur
            in higher-earning years. The first-year contribution falls from $22,283
            to $19,779, and the savings rate from 22.28% to 19.78%.
          </p>
          <div className="looking-ahead">
            <h3>Looking ahead</h3>
            <p>Next, we consider preserving the retirement fund indefinitely and financing retirement entirely from investment returns.</p>
          </div>
        </section>

        <section id="young-stage-3" className={`example-stage ${activeStage === 3 ? "" : "stage-hidden"}`} role="tabpanel">
          <StageHeader number="3" title="Capital Preservation and Perpetual Income" />
          <p>
            A fund designed to be spent down requires estimating the length of
            retirement and creates longevity risk. Under a capital-preservation
            strategy, withdrawals are limited to the annual real return, allowing
            the principal and retirement income to continue indefinitely.
          </p>
          <div className="formula-card">
            <span>Required Fund</span>
            <strong>Annual Retirement Income ÷ Real Rate of Return</strong>
          </div>
          <ChangesTable rows={[["Retirement Fund Objective", "Fund exhausted during retirement", "Fund preserved indefinitely"]]} />
          <BottomLine>
            <p>
              Generating <strong>$80,000 per year indefinitely</strong> requires
              approximately <strong>$2,000,000</strong>. You would need to save
              approximately <strong>$31,653 in the first year</strong>, or about
              <strong> 31.65% of current income</strong>, then increase that
              contribution with the assumed 1% real income growth.
            </p>
          </BottomLine>
          <h3>Understanding the results</h3>
          <p>
            The objective has changed: principal must remain intact, so only its
            return can support spending. At a 4% real return, $2,000,000 generates
            $80,000 per year. This protects against longevity risk and preserves
            assets, but requires considerably more saving.
          </p>
          <div className="looking-ahead">
            <h3>Looking ahead</h3>
            <p>Next, we examine how $100,000 of existing retirement savings reduces future contributions under both fund objectives.</p>
          </div>
        </section>

        <section id="young-stage-4" className={`example-stage ${activeStage === 4 ? "" : "stage-hidden"}`} role="tabpanel">
          <StageHeader number="4" title="Incorporating Existing Savings" />
          <p>
            Suppose you already have <strong>$100,000 invested for retirement</strong>.
            Because these funds are already working toward your goal, future
            contributions will be lower. We compare both retirement objectives.
          </p>
          <ChangesTable rows={[["Current Retirement Savings", "$0", "$100,000"]]} />
          <BottomLine>
            <h3>Fund exhausted during retirement</h3>
            <p>
              The target remains <strong>$1,249,766</strong>. With $100,000 already
              invested, first-year savings fall to <strong>$14,646</strong>, or
              about <strong>14.65% of current income</strong>.
            </p>
            <h3>Fund preserved indefinitely</h3>
            <p>
              The target remains <strong>$2,000,000</strong>. First-year savings
              fall to <strong>$26,520</strong>, or about <strong>26.52% of current
              income</strong>. Under either objective, contributions subsequently
              increase with 1% real income growth.
            </p>
          </BottomLine>
          <h3>Understanding the results</h3>
          <p>
            The goals are unchanged, but $100,000 now compounds during the remaining
            30 years and finances part of either target. First-year savings fall
            from $19,779 to $14,646 under fund exhaustion and from $31,653 to
            $26,520 under capital preservation.
          </p>
          <div className="looking-ahead">
            <h3>Looking ahead</h3>
            <p>Finally, we incorporate inflation and express future income and contributions in nominal dollars.</p>
          </div>
        </section>

        <section id="young-stage-5" className={`example-stage ${activeStage === 5 ? "" : "stage-hidden"}`} role="tabpanel">
          <StageHeader number="5" title="Incorporating Inflation" />
          <p>
            To reflect a more realistic environment, suppose inflation averages
            <strong> 3% per year</strong> throughout both working years and retirement.
          </p>
          <ChangesTable rows={[["Inflation Rate", "0.0%", "3.0%"]]} />
          <h3>How the model incorporates inflation</h3>
          <p>
            Future income, retirement income, contributions, and fund balances are
            expressed in nominal dollars. The model converts the 4% real return to
            its nominal equivalent. Because income also grows 1% in real terms,
            future income and savings rise with both real growth and inflation.
          </p>
          <BottomLine>
            <h3>Fund exhausted during retirement</h3>
            <p>
              First-year savings remains approximately <strong>$14,646</strong>,
              representing <strong>14.65% of current income</strong>. Future dollar
              contributions rise, while the percentage remains 14.65%.
            </p>
            <h3>Fund preserved indefinitely</h3>
            <p>
              First-year savings remains approximately <strong>$26,520</strong>,
              representing <strong>26.52% of current income</strong>. Future dollar
              contributions rise, while the percentage remains 26.52%.
            </p>
          </BottomLine>
          <h3>Understanding the results</h3>
          <p>
            Inflation makes future nominal amounts larger, but affects income and
            savings consistently. When inflation is incorporated consistently and
            the real return is unchanged, it changes future dollar amounts without
            changing the required savings percentage.
          </p>

          <h3>Conclusion</h3>
          <p>This example has progressively incorporated additional elements of retirement planning:</p>
          <ul className="example-conclusion-list">
            <li>constant income;</li>
            <li>real income growth;</li>
            <li>capital preservation;</li>
            <li>existing retirement savings; and</li>
            <li>inflation.</li>
          </ul>
          <p>
            The results demonstrate how each assumption affects the retirement plan.
            Income growth and existing savings reduce the amount that must be
            contributed from current income. Capital preservation increases the
            required retirement fund and savings rate. Inflation increases future
            dollar amounts but leaves the required savings percentage unchanged when
            it is incorporated consistently.
          </p>
          <p>
            As your circumstances evolve, you can revisit the simulator and update
            your income, accumulated savings, expected returns, retirement income
            goals, and economic assumptions. Your retirement plan can then evolve
            along with your financial life.
          </p>
        </section>

        <nav className="stage-pagination" aria-label="Navigate between stages">
          <button type="button" onClick={() => selectStage(activeStage - 1)} disabled={activeStage === 1}>
            <span aria-hidden="true">←</span> Previous stage
          </button>
          <span>Stage {activeStage} of {stages.length}</span>
          <button type="button" onClick={() => selectStage(activeStage + 1)} disabled={activeStage === stages.length}>
            Next stage <span aria-hidden="true">→</span>
          </button>
        </nav>
      </div>}
    </article>
  );
}

export default YoungProfessionalExample;
